/*
 * Wake Lock module using the Screen Wake Lock API
 * https://developer.mozilla.org/en-US/docs/Web/API/WakeLock
 *
 * For debugging, use the following in the console:
 * localStorage.debug='WakeLock'
 */

import debug from 'debug'

const dbg = debug('WakeLock')

const isSupported = 'wakeLock' in navigator

let wakeLock = null
let isActive = false

/*
 * Acquire a wake lock
 */
const acquire = async () => {
  if (!isSupported) {
    dbg('Wake Lock API not supported in this browser')
    return
  }

  if (isActive) {
    dbg('Wake Lock active')
    return
  }

  try {
    wakeLock = await navigator.wakeLock.request('screen')
    isActive = true
    wakeLock.addEventListener('release', () => {
      isActive = false
      dbg('Wake Lock released (event)')
    })
    dbg('Wake Lock acquired')
  } catch (err) {
    isActive = false
    dbg('Failed to acquire Wake Lock', err)
  }
}

/*
 * Release the wake lock
 */
const release = async () => {
  if (!isSupported) {
    dbg('Wake Lock API not supported in this browser')
    return
  }

  if (!isActive) {
    dbg('Wake Lock inactive')
    return
  }

  if (wakeLock && isActive) {
    try {
      await wakeLock.release()
      wakeLock = null
      isActive = false
      dbg('Wake Lock released')
    } catch (err) {
      dbg('Failed to release Wake Lock', err)
    }
  }
}

/*
 * Re-acquire the wake lock on visibility change
 * When the user switches tabs or minimizes the browser window,
 * the document's visibility state changes to 'hidden', and some
 * browsers will release any active wake locks.
 */
const handleVisibilityChange = () => {
  if (document.visibilityState === 'visible' && isActive) {
    acquire()
  }
}

if (isSupported) {
  document.addEventListener('visibilitychange', handleVisibilityChange)
}

export default {
  acquire,
  release
}
